import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dropdown,
  Loader,
  Segment,
  Card,
  CardContent,
  CardHeader,
} from "semantic-ui-react";
import { collegeProgramApi } from "@api/colleges/colleges";
import { withState } from "react-searchkit";

const CascadingDropdownFilter = ({ currentQueryState, updateQueryState }) => {
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("");
  const [availablePrograms, setAvailablePrograms] = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await collegeProgramApi.listCollegesWithPrograms();
        const collegesData = response.data.data;

        const departmentData = collegesData.map((college) => ({
          key: college.id,
          text: college.name,
          value: college.id,
        }));
        setDepartments(departmentData);

        const programData = collegesData.flatMap((college) =>
          (college.programs || []).map((program) => ({
            key: program.id,
            text: program.name,
            value: program.code,
            college_id: college.id,
          }))
        );
        setAllPrograms(programData);
      } catch (error) {
        console.error("Failed to fetch colleges and programs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (selectedDepartment && allPrograms.length > 0) {
      const filteredPrograms = allPrograms
        .filter((program) => program.college_id === selectedDepartment)
        .map((program) => ({
          key: program.key,
          text: program.text,
          value: program.value,
        }));

      setAvailablePrograms(filteredPrograms);
    } else {
      setAvailablePrograms([]);
      if (selectedDepartment === "") {
        setSelectedProgram("");
      }
    }
  }, [selectedDepartment, allPrograms]);

  useEffect(() => {
    const currentFilters = currentQueryState.filters || [];

    const departmentFilter = currentFilters.find(
      (filter) => filter[0] === "department"
    );
    const programFilter = currentFilters.find(
      (filter) => filter[0] === "program"
    );

    if (departmentFilter && departmentFilter[1] !== selectedDepartment) {
      setSelectedDepartment(departmentFilter[1]);
    } else if (!departmentFilter && selectedDepartment) {
      setSelectedDepartment("");
    }

    if (programFilter && programFilter[1] !== selectedProgram) {
      setSelectedProgram(programFilter[1]);
    } else if (!programFilter && selectedProgram) {
      setSelectedProgram("");
    }
  }, [currentQueryState.filters, selectedDepartment, selectedProgram]);

  const updateFilters = (field, value) => {
    const currentFilters = currentQueryState.filters || [];
    let newFilters = [...currentFilters];

    newFilters = newFilters.filter((filter) => filter[0] !== field);

    if (value) {
      newFilters.push([field, value]);
    }

    const newQueryState = {
      ...currentQueryState,
      filters: newFilters,
      page: 1,
    };

    updateQueryState(newQueryState);
  };

  const handleDepartmentChange = (event, { value }) => {
    setSelectedDepartment(value || "");

    if (selectedProgram) {
      setSelectedProgram("");
      updateFilters("program", null);
    }

    updateFilters("department", value);
  };

  const handleProgramChange = (event, { value }) => {
    setSelectedProgram(value || "");
    updateFilters("program", value);
  };

  if (loading) {
    return (
      <Segment>
        <Loader active inline="centered" size="small" />
        <div style={{ textAlign: "center", marginTop: "10px" }}>
          Loading vocabularies...
        </div>
      </Segment>
    );
  }

  return (
    <Card>
      <CardContent>
        <CardHeader as="h4">Department</CardHeader>
        <Dropdown
          placeholder="Select Department"
          fluid
          search
          selection
          clearable
          options={departments}
          value={selectedDepartment}
          onChange={handleDepartmentChange}
        />
      </CardContent>

      <CardContent>
        <CardHeader as="h4">Program</CardHeader>
        <Dropdown
          placeholder={
            selectedDepartment ? "Select Program" : "Select Department First"
          }
          fluid
          search
          selection
          clearable
          disabled={!selectedDepartment}
          options={availablePrograms}
          value={selectedProgram}
          onChange={handleProgramChange}
        />
      </CardContent>
    </Card>
  );
};

CascadingDropdownFilter.propTypes = {
  currentQueryState: PropTypes.object.isRequired,
  updateQueryState: PropTypes.func.isRequired,
};

export default withState(CascadingDropdownFilter);
